import { useEffect, useState } from "react";
import axios from "axios";
import { Clock, Flame, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = "kacinoe_promo_dismissed_v1";

function computeRemaining(endDate) {
  const end = new Date(endDate).getTime();
  const now = Date.now();
  const diff = end - now;
  if (diff <= 0) return null;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds };
}

function pad(n) {
  return String(n).padStart(2, "0");
}

export default function PromoBar() {
  const [promo, setPromo] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function fetchPromo() {
      try {
        const res = await axios.get(`${API}/promo`);
        if (cancelled) return;
        setPromo(res.data);
        // Check dismissed key against promo id (so a new campaign resets dismissal)
        const dismissedId = localStorage.getItem(DISMISS_KEY);
        if (res.data && dismissedId === res.data.id) setDismissed(true);
      } catch (err) {
        // No promo or error - silently ignore
      }
    }
    fetchPromo();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!promo || !promo.enabled || !promo.end_date) {
      setRemaining(null);
      return;
    }
    const tick = () => setRemaining(computeRemaining(promo.end_date));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [promo]);

  const handleDismiss = () => {
    if (promo) localStorage.setItem(DISMISS_KEY, promo.id);
    setDismissed(true);
  };

  if (!promo || !promo.enabled || !remaining || dismissed) return null;

  return (
    <div
      data-testid="promo-bar"
      className="relative bg-gradient-to-r from-[#6C3FC5] via-[#7B4ED4] to-[#582FB0] text-white overflow-hidden"
      role="region"
      aria-label="Offre promotionnelle"
    >
      {/* Animated shimmer */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%)",
          backgroundSize: "200% 100%",
          animation: "promoShimmer 3s linear infinite",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5">
        <div className="flex items-center justify-center gap-3 sm:gap-5 flex-wrap text-center">
          <div className="flex items-center gap-2 text-sm sm:text-base">
            <Flame size={16} className="text-[#FFD166] animate-pulse" />
            <span className="font-bold" data-testid="promo-title">
              {promo.title}
            </span>
            {promo.subtitle && (
              <span className="hidden sm:inline opacity-90 text-sm">— {promo.subtitle}</span>
            )}
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <Clock size={14} className="opacity-80" />
            <div className="flex items-center gap-1 font-mono font-bold" data-testid="promo-countdown">
              {remaining.days > 0 && (
                <>
                  <TimeBlock value={remaining.days} label="j" />
                  <span className="opacity-60">:</span>
                </>
              )}
              <TimeBlock value={pad(remaining.hours)} label="h" />
              <span className="opacity-60">:</span>
              <TimeBlock value={pad(remaining.minutes)} label="m" />
              <span className="opacity-60">:</span>
              <TimeBlock value={pad(remaining.seconds)} label="s" pulse />
            </div>
          </div>
        </div>

        <button
          onClick={handleDismiss}
          data-testid="promo-dismiss"
          aria-label="Fermer la promotion"
          className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
        >
          <X size={14} />
        </button>
      </div>

      <style>{`
        @keyframes promoShimmer {
          0% { background-position: -100% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  );
}

function TimeBlock({ value, label, pulse }) {
  return (
    <span
      className={`inline-flex items-baseline bg-white/15 backdrop-blur-sm rounded-md px-1.5 py-0.5 text-xs sm:text-sm tabular-nums ${
        pulse ? "animate-pulse" : ""
      }`}
    >
      {value}
      <span className="opacity-60 text-[10px] ml-0.5">{label}</span>
    </span>
  );
}
